import i18n from 'locales/i18n';

class Facade {

    /*****************************************************
     * List of functions helping managing url paramaters *
     *****************************************************/

    getAllUrlParams() {
        const searchParams = new URLSearchParams(window.location.search);

        const returnParams = [];

        // Iterate on the search parameters.
        for (const p of searchParams) {
            returnParams.push(p);
        }

        return returnParams;
    }

    getUrlParam(name) {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get(name);
    }

    setAllUrlParams(params) {
        for (const p of Object.keys(params)) {
            if (params[p].length !== 0 && params[p] !== undefined && params[p])
                this.setUrlParam(p, params[p]);
        }
    }

    setUrlParam(name, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(name);

        if (value)
            searchParams.append(name, value);

        window.history.replaceState({}, '', window.location.pathname + '?' + searchParams);
    }

    deleteUrlParam(name) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(name);
        window.history.replaceState({}, '', window.location.pathname + '?' + searchParams);
    }

    deleteAllUrlParams() {
        const searchParams = new URLSearchParams(window.location.search);

        for (const p of searchParams) {
            searchParams.delete(p[0]);
        }

        window.history.replaceState({}, '', window.location.pathname);
    }

    /****************************************
     * List of functions helping developers *
     ****************************************/

    formatDate(date) {
        if (!(date instanceof Date))
            return '';

        const language = i18n.getCurrentLanguage();

        let monthNames = [
            'Jan', 'Feb', 'Mar',
            'Apr', 'May', 'Jun', 'Jul',
            'Aug', 'Sep', 'Oct',
            'Nov', 'Dec'
        ];

        if (language === 'fr') {
            monthNames = [
                'Jan', 'Fev', 'Mars',
                'Avr', 'Mai', 'Juin', 'Jui',
                'Août', 'Sep', 'Oct',
                'Nov', 'Dec'
            ];
        }

        const day = date.getDate();
        const monthIndex = date.getMonth();
        const year = date.getFullYear();

        return day + ' ' + monthNames[monthIndex] + ', ' + year;
    }

    formatDateForUrl(date) {
        return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    }

    getTodayDateStr() {
        const dt = new Date();

        const year = dt.getFullYear();
        const month = (dt.getMonth() + 1).toString().padStart(2, '0');
        const day = dt.getDate().toString().padStart(2, '0');

        return year + '-' + month + '-' + day;
    }

    /**
     * Method which return the name to display of a website
     *
     * @param name <string> name of the site
     * @returns <string> name to display of the site
     */
    siteDisplayName(name) {
        if (!name || typeof name !== 'string') return '';

        if (name.includes('zzzz')) {
            return name;
        }

        if (name.startsWith('YV_')) {
            return name.split('_')[3];
        }

        const nameSplitted = name.split('_');
        const displayName = nameSplitted.length > 1 ? nameSplitted[0] + '.' + nameSplitted[1] : name;

        return displayName;
    }

    // Manage PriceComparator URL
    getConfig(url) {
        try {
            const decodedUrl = decodeURI(url);
            if (decodedUrl.includes('bw_config={')) {
                const params = [];
                const parseParams = (paramString) => {
                    const paramsFound = paramString.match(/\[([^,[\]]*),([^[\]]*)\]/);
                    if (paramsFound) {
                        const key = (paramsFound[1] + '').trim().toLowerCase();
                        const value = (paramsFound[2] + '').trim().toLowerCase();
                        params.push({ key, value });
                        parseParams(paramString.substring(paramsFound.index + paramsFound[0].length));
                    }
                };
                const decodedComponents = decodeURIComponent(decodedUrl.split('bw_config={')[1].split('}')[0]);
                parseParams(decodedComponents);
                return params;
            }
            return [];

        } catch (e) {
            console.log('%c Error URI Malformed: ' + url, 'background: #ad0c00; color: #ffffff');
            return [];
        }
    }

    getRef(url) {
        try {
            const decodedUrl = decodeURI(url);
            const refExtractor = /bw_ref=([^&]*)/;
            const extractedParams = decodedUrl.match(refExtractor);
            if (extractedParams && extractedParams[1]) {
                return decodeURIComponent(extractedParams[1]).trim().toLowerCase();
            }
            return undefined;

        } catch (e) {
            return undefined;
        }
    }

    getAdd(url) {
        try {
            const decodedUrl = decodeURI(url);
            const addExtractor = /bw_add=([\d,.]+)/;
            const extractedParams = decodedUrl.match(addExtractor);
            if (extractedParams && extractedParams[1]) {
                return parseFloat(extractedParams[1].replace(',', '.'));
            }
            return undefined;

        } catch (e) {
            return undefined;
        }
    }

    getMul(url) {
        try {
            const decodedUrl = decodeURI(url);
            const mulExtractor = /bw_mul=([\d,.]+)/;
            const extractedParams = decodedUrl.match(mulExtractor);
            if (extractedParams && extractedParams[1]) {
                return parseFloat(extractedParams[1].replace(',', '.'));
            }
            return undefined;

        } catch (e) {
            return undefined;
        }
    }

    // Return api root
    getApiRoot(apiName) {
        if (!apiName) {
            if (process.env.REACT_APP_ENV === 'production') {
                return process.env.REACT_APP_API_ROOT_PROD;
            }
            return process.env.REACT_APP_API_ROOT_DEV;
        }
        return process.env.REACT_APP_API_ROOT_PROD;

    }

    getEnv() {
        return process.env.REACT_APP_ENV;
    }

    firstLetterInCapital(str) {
        if (str)
            return str.charAt(0).toUpperCase() + str.slice(1);
        return str;
    }

    isEmpty(obj) {

        // null and undefined are "empty"
        if (!obj) return true;

        // Assume if it has a length property with a non-zero value
        // that that property is correct.
        if (obj.length > 0) return false;
        if (obj.length === 0) return true;

        // If it isn't an object at this point
        // it is empty, but it can't be anything *but* empty
        // Is it empty?  Depends on your application.
        if (typeof obj !== 'object') return true;

        // Otherwise, does it have any properties of its own?
        // Note that this doesn't handle
        // toString and valueOf enumeration bugs in IE < 9
        for (const key in obj) {
            if (key in obj) return false;
        }

        return true;
    }

    isArray = (a) => Array.isArray(a);

    isObject = (o) => o === Object(o) && !this.isArray(o) && typeof o !== 'function';

    toCamel = (s) => s.replace(/([-_][a-z])/ig, ($1) => $1.toUpperCase().replace('-', '').replace('_', ''));

    keysToCamel = (o) => {
        if (this.isObject(o)) {
            const n = {};

            Object.keys(o)
                .forEach((k) => {
                    if (!k.includes('zzzz')) {
                        n[this.toCamel(k)] = this.keysToCamel(o[k]);
                    } else {
                        n[k] = this.keysToCamel(o[k]);
                    }
                });

            return n;
        } else if (this.isArray(o)) return o.map((i) => this.keysToCamel(i));

        return o;
    };

    formatUrl(url) {
        if (!url || typeof url !== 'string') return '';

        if (url.startsWith('http')) {
            return url;
        }

        const formattedUrl = `https://${url}`;

        return formattedUrl;
    }

    openUrl(url) {
        window.open(this.formatUrl(url), '_blank', 'noreferrer');
    }
}

export default new Facade();
